import {
  action, computed, makeObservable, observable,
} from 'mobx';
import { Modeconstraint } from '@api-consent/schema/Enums.js';
import { featuresTypes } from '@src/defs/featureTypes.js';
import { FeatureValueStore } from '@src/state/FeatureValueStore.js';
import { Consent, FeatureType } from '@src/types/comsApi.js';
import { ModifiedState, TefComs } from '@src/types/typings.js';
import { stripHtml } from '@src/util/helpers.js';

/**
 * The ConsentStore class represents a consent object used to manage granted permissions,
 * associated types, configuration, and their features within a system. It implements
 * mechanisms to track and modify the consent and perform related actions.
 */
export class ConsentStore implements Consent {
  channels?: FeatureValueStore[] = undefined;
  client?: string;
  clientDisplay?: string;
  configId?: string;
  dataTypes?: FeatureValueStore[] = undefined;
  date?: string;
  deprecated?: boolean;
  description?: string;
  granted?: boolean = undefined;
  originalGranted?: boolean = undefined;
  initialGranted?: boolean = undefined;
  hintAfter?: string;
  hintBefore?: string;
  id: string;
  name?: string;
  optout?: boolean;
  productTypes?: FeatureValueStore[] = undefined;
  purposeTypes?: FeatureValueStore[] = undefined;
  scope?: string;
  shortScope?: string;
  version?: string;
  modeconstraint?: Modeconstraint;

  private $shortInfo?: string;

  constructor({
    id, shortInfo, granted, originalGranted, initialGranted, ...restData
  }: Consent) {
    makeObservable(this, {
      channels: observable,
      dataTypes: observable,
      granted: observable,
      productTypes: observable,
      purposeTypes: observable,
      setGranted: action,
      resetToInitial: action,
      change: action,
      features: computed,
      hasGrantedFeature: computed,
      hasNotGrantedFeature: computed,
      hasAllFeaturesUndefined: computed,
      shortInfo: computed,
      featureTitlesAsString: computed,
      modified: computed,
      modifiedFromInitialSelfOrOneOfTheFeatures: computed,
    });
    this.id = id;
    this.$shortInfo = shortInfo;
    this.granted = granted;
    this.originalGranted = originalGranted;
    this.initialGranted = initialGranted;
    Object.assign(this, restData);
    featuresTypes.forEach((featureName) => {
      if (restData[featureName]) {
        this[featureName] = restData[featureName]?.map(
          (f) => new FeatureValueStore(f, this),
        );
      }
    });
    this.setGrantedAccordingToFeatures();
  }

  setGrantedAccordingToFeatures() {
    if (this.features.length > 0 && !this.hasAllFeaturesUndefined) {
      this.setGranted(this.hasGrantedFeature);
    }
  }

  change(value: boolean): void {
    if (value !== this.granted) {
      this.setGrantedWithFeatures(value);
    }
  }

  setGrantedWithFeatures(granted: boolean): void {
    this.setGranted(granted);
    this.features.forEach((feature) => {
      feature.setGranted(granted);
    });
  }

  setGranted(granted: boolean): void {
    this.granted = granted;
  }

  /**
   * Resets the state of the object and its features to their initial values.
   *
   * @return {void} No return value.
   */
  resetToInitial() {
    this.granted = this.initialGranted;
    this.features.forEach((feature) => {
      feature.resetToInitial();
    });
  }

  get features(): FeatureValueStore[] {
    // we are assuming that there is always only *one* feature attached
    return this.featureType
      ? (this[this.featureType] as FeatureValueStore[])
      : [];
  }

  get featureType(): FeatureType | undefined {
    // we are assuming that there is always only *one* feature attached
    return featuresTypes.find(
      (featureName: FeatureType) => !!this[featureName],
    );
  }

  get hasGrantedFeature(): boolean {
    return !!this.features.find((f) => f.granted);
  }

  get hasNotGrantedFeature(): boolean {
    return !!this.features.find((f) => !f.granted) && !this.hasAllFeaturesUndefined;
  }

  get hasAllFeaturesUndefined(): boolean {
    return !this.features.some((feature) => feature.isRevokedOrGranted);
  }

  /**
   * Determines the modified state based on the current granted state compared to the original granted state.
   * If the granted state has not changed from its original state, it returns undefined.
   * If the granted state has been updated, it returns 'granted' if granted is true, and 'revoked' if granted is false.
   *
   * @return {ModifiedState | undefined} The modified state as 'granted', 'revoked', or undefined if there is no change.
   */
  get modified(): ModifiedState | undefined {
    if (this.granted === this.originalGranted) {
      return undefined;
    }

    return this.granted ? 'granted' : 'revoked';
  }

  /**
   * Determines if the current object or any of its associated features are modified from its initial state.
   *
   * This getter checks whether the `modified` property of the object itself is true
   * or if any of the `features` associated with the object have the `modified` property set to true.
   *
   * @return {boolean} Returns true if the object itself is modified or if any of the features are modified; otherwise, returns false.
   */
  get modifiedFromInitialSelfOrOneOfTheFeatures(): boolean {
    return this.granted !== this.initialGranted || this.features.some((f) => f.modified);
  }

  /**
   * Counts the number of items marked as modified, including the current object and its features.
   *
   * @param {ModifiedState} modified - The modification state to compare against.
   * @return {number} The count of items with the specified modification state.
   */
  countModified(modified: ModifiedState): number {
    let counter = this.modified === modified ? 1 : 0;
    this.features.forEach((f) => {
      if (f.modified === modified) {
        counter += 1;
      }
    });

    return counter;
  }

  /**
   * Checks if the current consent has been either granted or revoked and
   * verifies that all associated features are also either granted or revoked.
   *
   * @return {boolean} Returns true if the current instance is granted or revoked and all features meet the same condition; otherwise, false.
   */
  get isRevokedOrGranted(): boolean {
    return typeof this.granted !== 'undefined' && this.features.every((f) => f.isRevokedOrGranted);
  }

  get shortInfo(): string | undefined {
    const re = /(<span class="coms-features-\w+">)(.*)(<\/span>)/;
    return this.$shortInfo?.replace(re, this.featureTitlesAsString ? `$1${this.featureTitlesAsString}$3` : '$1$3');
  }

  get featureTitlesAsString(): string {
    return this.features
      .filter((f) => f.granted && f.title)
      .map((f) => stripHtml(f.title))
      .join(', ');
  }

  public toJSON(): Consent {
    const ret: Consent = {
      ...this,
    };

    featuresTypes.forEach((featureType) => {
      if (this[featureType]) {
        ret[featureType] = this[featureType]?.map((feature) => feature.toJSON());
      }
    });

    return ret;
  }

  public toSaveConsent(): TefComs.ConsentStatus {
    const {
      id, granted = false, featureType, features,
    } = this;
    return {
      type: 'consent',
      id,
      granted,
      ...(featureType
        ? { [featureType]: features.map((feature) => feature.toSaveFeatureValue()) }
        : {}
      ),
    };
  }

  public toShortConsent(): TefComs.ShortConsent {
    const {
      id,
      granted = false,
      featureType,
      features,
      shortInfo = '',
      version = '',
    } = this;
    return {
      id,
      granted,
      ...(featureType
        ? { [featureType]: features.map((feature) => feature.toShortFeatureValue()) }
        : {}
      ),
      shortInfo,
      version,
    };
  }
}
